import dayjs from "./dayjs";

type WeekIndexType = 0 | 1 | 2 | 3 | 4 | 5 | 6;

interface PeriodType {
  start: Date;
  end: Date;
}

const toDayjsFormat = (formatPattern: string) =>
  formatPattern
    .replace(/do/g, "Do")
    .replace(/yyyy/g, "YYYY")
    .replace(/dd/g, "DD")
    .replace(/\ba\b/g, "A");

const parseTimeOnDate = (time: string, baseDate: Date): Date | null => {
  const normalizedTime = time.trim().toUpperCase();
  const base = dayjs(baseDate).format("YYYY-MM-DD");
  const formats = [
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DD H:mm",
    "YYYY-MM-DD hh:mm A",
    "YYYY-MM-DD h:mm A",
  ];

  for (const format of formats) {
    const parsed = dayjs(`${base} ${normalizedTime}`, format, true);
    if (parsed.isValid()) return parsed.toDate();
  }

  return null;
};

export function daysInMonth(date?: dayjs.ConfigType) {
  const daysNumber = dayjs(date || dayjs()).daysInMonth();

  if (!daysNumber) return 0;

  return daysNumber;
}

export function allDaysInMonth(date?: Date) {
  if (!dateIsValid(date || new Date())) return [];

  const maxDaysInMonth = daysInMonth(date);

  const days: Date[] = [];

  for (let i = 1; i <= maxDaysInMonth; i++) {
    days.push(dayjs(date).date(i).toDate());
  }

  return days;
}

export function firstDayOfMonth(date?: Date) {
  return dayjs(date || dayjs())
    .startOf("month")
    .toDate();
}

export function endDayOfMonth(date?: Date) {
  return dayjs(date || dayjs())
    .endOf("month")
    .toDate();
}

export function dayIndexInWeek(date?: Date) {
  return dayjs(date || dayjs()).day() as WeekIndexType;
}

export function dateIsValid(date: Date) {
  return dayjs(date).isValid();
}

export function previousDaysInWeek(dayInWeek: WeekIndexType, date: Date) {
  if (!dateIsValid(date)) return [];

  const previousDays: Date[] = [];

  for (let i = 0; i < dayInWeek; i++) {
    previousDays.push(dayjs(date).day(i).toDate());
  }

  return previousDays;
}

export function nextDaysInWeek(dayInWeek: WeekIndexType, date: Date) {
  if (!dateIsValid(date)) return [];

  const nextDays: Date[] = [];

  for (let i = dayInWeek + 1; i <= 6; i++) {
    nextDays.push(dayjs(date).day(i).toDate());
  }

  return nextDays;
}

export function previousMonthBy(date: Date) {
  if (!dateIsValid(date)) return dayjs().toDate();

  const parseDate = dayjs(date);

  return parseDate
    .date(1)
    .hour(0)
    .minute(0)
    .second(0)
    .month(parseDate.month() - 1)
    .toDate();
}

export function nextMonthBy(date: Date) {
  if (!dateIsValid(date)) return dayjs().toDate();

  const parseDate = dayjs(date);

  return parseDate
    .date(1)
    .hour(0)
    .minute(0)
    .second(0)
    .month(parseDate.month() + 1)
    .toDate();
}

export function dayOfDate(date: Date) {
  if (!dateIsValid(date)) return null;

  return dayjs(date).date();
}

export function dayOfWeek(date: Date) {
  if (!dateIsValid(date)) return null;

  return dayjs(date).format("dddd");
}

export function isCurrentDay(date: Date) {
  if (!dateIsValid(date)) return false;

  return dayjs(date).isToday();
}

export function formatDate(date: Date, format: string) {
  if (!dateIsValid(date)) return null;

  return dayjs(date).format(format);
}

export function isSunday(date: Date) {
  return dayIndexInWeek(date) === 0;
}

export function dateIsSame(a: Date, b: Date, unit: dayjs.OpUnitType) {
  if (!dateIsValid(a) || !dateIsValid(b)) return false;

  return dayjs(a).isSame(dayjs(b), unit);
}

export function dateIsSameOrBefore(a: Date, b: Date, unit: dayjs.OpUnitType) {
  if (!dateIsValid(a) || !dateIsValid(b)) return false;

  return dayjs(a).isSameOrBefore(dayjs(b), unit);
}

export function dateIsBefore(a: Date, b: Date, unit: dayjs.OpUnitType) {
  if (!dateIsValid(a) || !dateIsValid(b)) return false;

  return dayjs(a).isBefore(dayjs(b), unit);
}

export function dateIsAfter(a: Date, b: Date, unit: dayjs.OpUnitType) {
  if (!dateIsValid(a) || !dateIsValid(b)) return false;

  return dayjs(a).isAfter(dayjs(b), unit);
}

export function dateIsSameOrAfter(a: Date, b: Date, unit: dayjs.OpUnitType) {
  if (!dateIsValid(a) || !dateIsValid(b)) return false;

  return dayjs(a).isSameOrAfter(dayjs(b), unit);
}

export function dateIsBetween(
  whoIsBetween: Date,
  start: Date,
  end: Date,
  unit: dayjs.OpUnitType,
  include?: { start?: boolean; end?: boolean },
) {
  if (!dateIsValid(whoIsBetween) || !dateIsValid(start) || !dateIsValid(end)) {
    return false;
  }

  return dayjs(whoIsBetween).isBetween(
    dayjs(start),
    dayjs(end),
    unit,
    `${include?.start ? "[" : "("}${include?.end ? "]" : ")"}`,
  );
}

export function sortDates(dates: Date[]) {
  return dates.sort((a, b) => {
    if (dayjs(a).isSame(dayjs(b), "date")) return 0;

    if (dayjs(a).isSameOrBefore(dayjs(b), "date")) return -1;

    return 1;
  });
}

export function dateDiff(a: Date, b: Date, unit: dayjs.OpUnitType) {
  if (!dateIsValid(a) || !dateIsValid(b)) return 0;

  return dayjs(a).diff(dayjs(b), unit);
}

export function endOfDate(date: Date, unit: dayjs.OpUnitType) {
  if (!dateIsValid(date)) return null;

  return dayjs(date).endOf(unit).toDate();
}

export function dateAdd(date: Date, value: number, unit: dayjs.ManipulateType) {
  if (!dateIsValid(date)) return date;

  return dayjs(date).add(value, unit).toDate();
}

export function splitDatePeriodByWeek(period: PeriodType) {
  if (!dateIsValid(period.start) || !dateIsValid(period.end)) return [];

  const splitPeriods: PeriodType[] = [];

  let currentStart = period.start;
  let endWeek = endOfDate(currentStart, "week");

  while (!!endWeek && dateIsBefore(endWeek, period.end, "date")) {
    splitPeriods.push({
      start: currentStart,
      end: endWeek,
    });

    currentStart = dateAdd(endWeek, 1, "day");
    endWeek = endOfDate(currentStart, "week");
  }

  splitPeriods.push({
    start: currentStart,
    end: period.end,
  });

  return splitPeriods;
}

export function startOfWeek(date: Date) {
  return dayjs(date).startOf("week").toDate();
}

export function endOfWeek(date: Date) {
  return dayjs(date).endOf("week").toDate();
}

export function eachDayOfInterval(interval: { start: Date; end: Date }) {
  const days: Date[] = [];
  let current = dayjs(interval.start);

  while (current.isSameOrBefore(interval.end, "day")) {
    days.push(current.toDate());
    current = current.add(1, "day");
  }

  return days;
}

export function format2Date(timestamp: number | string | Date): string {
  return dayjs(timestamp).format("YYYY-MM-DD");
}

export function format3Date(ms: number) {
  if (!ms) return null;

  return dayjs(ms).format("MMM DD, YYYY");
}
/**
 * Formats a timestamp (ms) to 'YYYY-MM-DD HH:mm:ss'
 */
export function formatDateTime(timestamp: number | string | Date): string {
  return dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss");
}

export function formatDateTimeFancy(
  timestamp?: number | string | Date | null,
): string {
  if (!timestamp) return "—";

  const localTime = dayjs(timestamp).tz(dayjs.tz.guess());
  const datePart = localTime.format("ddd DD MMM");
  const timePart = localTime.format("hh:mm A");

  return `${datePart} at ${timePart}`;
}

export function formatDateShortTime(
  timestamp?: number | string | Date | null,
): string {
  if (!timestamp) return "—";

  const localTime = dayjs(timestamp).tz(dayjs.tz.guess());
  const datePart = localTime.format("MMM D"); // Jul 16
  const timePart = localTime.format("h:mm A"); // 5:51 PM

  return `${datePart}, ${timePart}`;
}

export function formatReadableDateTime(
  timestamp: number | string | Date,
): string {
  const safe =
    typeof timestamp === "string" && /^\d+$/.test(timestamp.trim())
      ? Number(timestamp.trim())
      : timestamp;
  const parsed = dayjs(safe);
  return parsed.isValid() ? parsed.format("MMM D YYYY h:mm A") : "Invalid Date";
}

/**
 * Formats a timestamp to "7 April 2026 4:30 PM" format.
 * Use this for PDF & Excel exports and anywhere a human-readable full date is needed.
 */
export function formatExportDateTime(
  timestamp?: number | string | Date | null,
): string {
  if (!timestamp) return "\u2014";
  const parsed = dayjs(timestamp);
  return parsed.isValid() ? parsed.format("DD MMMM YYYY h:mm A") : "\u2014";
}

/**
 * Formats a timestamp to "04 Mar 2025, 4:30 PM" format.
 * Use this for platform UI (tables, cards, lists).
 */
export function formatShortDateTime(
  timestamp?: number | string | Date | null,
): string {
  if (!timestamp) return "\u2014";
  const parsed = dayjs(timestamp);
  return parsed.isValid() ? parsed.format("DD MMM YYYY, h:mm A") : "\u2014";
}

export const toUtcISOString = (
  date?: Date | string | null,
): string | undefined => {
  if (!date) return undefined;
  return new Date(date).toISOString(); // Example: "2025-07-11T04:30:00.000Z"
};

export const formatLocalDate = (
  dateString?: string | Date | null,
  formatPattern = "dd/MM/yyyy hh:mm a",
): string => {
  if (!dateString) return "--";
  return dayjs(dateString).format(toDayjsFormat(formatPattern));
};

/**
 * Returns a GitHub-style relative time string: "just now", "5 minutes ago", "yesterday", "3 days ago", etc.
 */
export function formatTimeAgo(timestamp: number | string | Date): string {
  const safe =
    typeof timestamp === "string" && /^\d+$/.test(timestamp.trim())
      ? Number(timestamp.trim())
      : timestamp;
  const parsed = dayjs(safe);
  if (!parsed.isValid()) return "Invalid Date";
  // Guard against future timestamps (server clock skew) — treat as "just now"
  if (parsed.isAfter(dayjs())) return "a few seconds ago";
  return parsed.fromNow();
}

/**
 * Full date for tooltip: "Apr 14, 2026 12:03 PM"
 */
export function formatFullDateTime(timestamp: number | string | Date): string {
  const safe =
    typeof timestamp === "string" && /^\d+$/.test(timestamp.trim())
      ? Number(timestamp.trim())
      : timestamp;
  const parsed = dayjs(safe);
  return parsed.isValid()
    ? parsed.format("MMM D, YYYY h:mm A")
    : "Invalid Date";
}

// Calculate the next run Date from a test plan schedule

type RepeatUnit = "DAY" | "WEEK" | "MONTH";

type EndCondition = {
  type: "NEVER" | "ON" | "AFTER";
  onDate: number | null;
  afterOccurrences: number | null;
};

type TestPlanScheduleParams = {
  id: number;
  testPlanId: number;
  name: string;
  startDate: number;
  endDate: number | null;
  isRecurring: boolean;
  repeatEvery?: number;
  repeatDays?: number[] | null;
  repeatUnit?: RepeatUnit | null;
  timeSlots: string[];
  endCondition: EndCondition;
};

export const getNextRun = (
  schedule: TestPlanScheduleParams,
  now: Date = new Date(),
): Date | null => {
  if (!schedule || !schedule.startDate || !schedule.timeSlots?.length)
    return null;

  const {
    startDate,
    endDate,
    isRecurring,
    repeatEvery = 1,
    repeatUnit = "DAY",
    repeatDays = [],
    timeSlots,
    endCondition,
  } = schedule;

  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : null;

  // 1. Handle one-time schedule
  if (!isRecurring) {
    const slotTime = parseTimeOnDate(timeSlots[0], start);
    return slotTime && dayjs(slotTime).isAfter(now) ? slotTime : null;
  }

  // 2. Recurring schedule
  let candidate = new Date(now);

  // Make sure candidate is never before the start
  if (dayjs(start).isAfter(candidate)) candidate = start;

  let occurrences = 0;

  while (true) {
    // Check end date/time limit
    if (end && dayjs(candidate).isAfter(end)) return null;
    if (
      endCondition?.type === "ON" &&
      endCondition.onDate &&
      dayjs(candidate).isAfter(new Date(endCondition.onDate))
    )
      return null;
    if (
      endCondition?.type === "AFTER" &&
      endCondition.afterOccurrences !== null
    ) {
      if (occurrences >= endCondition.afterOccurrences) return null;
    }

    if (repeatUnit === "DAY") {
      for (const time of timeSlots) {
        const runTime = parseTimeOnDate(time, candidate);
        if (runTime && dayjs(runTime).isAfter(now)) {
          occurrences++;
          return runTime;
        }
      }
      candidate = dayjs(candidate).add(repeatEvery, "day").toDate();
    } else if (repeatUnit === "WEEK") {
      // Loop this and next few weeks until a match is found
      for (let offset = 0; offset < 7 * repeatEvery; offset++) {
        const day = dayjs(candidate).add(offset, "day").toDate();
        if (repeatDays?.includes(day.getDay())) {
          for (const time of timeSlots) {
            const runTime = parseTimeOnDate(time, day);
            if (runTime && dayjs(runTime).isAfter(now)) {
              occurrences++;
              return runTime;
            }
          }
        }
      }
      candidate = dayjs(candidate).add(repeatEvery, "week").toDate();
    } else if (repeatUnit === "MONTH") {
      for (let i = 0; i < 31; i++) {
        const date = dayjs(candidate).add(i, "day").toDate();
        if (repeatDays?.includes(date.getDay())) {
          for (const time of timeSlots) {
            const runTime = parseTimeOnDate(time, date);
            if (runTime && dayjs(runTime).isAfter(now)) {
              occurrences++;
              return runTime;
            }
          }
        }
      }
      candidate = dayjs(candidate).add(repeatEvery, "month").toDate();
    } else {
      return null;
    }
  }
};

/**
 * Returns the effective display status for a schedule:
 * - "COMPLETED" if it's a one-time schedule whose run time has already passed
 * - Otherwise returns the stored status as-is
 */
export const getEffectiveScheduleStatus = (schedule: any): string => {
  if (!schedule?.isRecurring && getNextRun(schedule) === null) {
    return "COMPLETED";
  }
  return schedule?.status ?? "ACTIVE";
};

export const formatDuration = (ms: number) => {
  const seconds = Math.floor(ms / 1000);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h.toString().padStart(2, "0")}:${m
    .toString()
    .padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

/**
 * Format timestamp as relative time (e.g., "Just now", "2m ago", "1h ago")
 * Falls back to formatReadableDateTime if the timestamp is too old
 */
export function formatRelativeTime(timestamp: number | string | Date): string {
  const safe =
    typeof timestamp === "string" && /^\d+$/.test(timestamp.trim())
      ? Number(timestamp.trim())
      : timestamp;
  const date = dayjs(safe);
  const now = dayjs();

  if (!date.isValid()) return "Unknown";

  const diffSeconds = now.diff(date, "second");
  const diffMinutes = now.diff(date, "minute");
  const diffHours = now.diff(date, "hour");
  const diffDays = now.diff(date, "day");

  // Just now (within 1 minute)
  if (diffSeconds < 60) {
    return "Just now";
  }

  // Minutes (1-59)
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }

  // Hours (1-23)
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  // Days (1-6)
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }

  // Fall back to full date for older timestamps
  return formatReadableDateTime(timestamp);
}
